/**
 * Факты для свидетельства о смерти: общие для экрана (DeathScreen) и
 * карточки для шеринга (share/card.ts). Только чтение `Life`; все строки —
 * через `t()`.
 *
 * Страна рождения в `Life` не хранится отдельно: берётся из первой записи
 * журнала `birth.born` (там название на русском), иначе — текущая страна.
 * Переезды считаются по записям журнала о состоявшихся переездах.
 */
import { getCountry, getValue, type Dataset } from '@life/data';
import { FLAG_JOB_START_AGE, FLAG_RETIRED, type Life } from '@life/engine';
import { t, yearsWord } from '@life/i18n';
import { childrenWord, years } from './format';
import { FLAG_KEY_YEARS, FLAG_LAST_WORDS } from '../state/game';

const BIRTH_KEY = 'birth.born';
const MOVE_KEYS: ReadonlySet<string> = new Set(['decision.move.city.move', 'decision.move.abroad.go.success']);
const RETIRE_KEY = 'decision.work.retire.retire';

export interface CertificateFacts {
  name: string;
  /** «Нигер → Нигерия · 2004 — 2071» или «Нигер · 2004 — 2071». */
  span: string;
  lived: string;
  cause: string;
  education: string;
  work: string;
  family: string;
  moves: string;
}

function countryName(ds: Dataset, iso3: string): string {
  return ds.countries.find((c) => c.iso3 === iso3)?.nameRu ?? iso3;
}

/** Страна рождения на русском: из записи о рождении, иначе текущая. */
export function birthCountry(life: Life, ds: Dataset): string {
  const born = life.log.find((e) => e.key === BIRTH_KEY);
  const fromLog = born?.params?.['country'];
  return typeof fromLog === 'string' && fromLog.length > 0 ? fromLog : countryName(ds, life.iso3);
}

/** Сколько раз состоялся переезд (в город или за границу). */
export function movesCount(life: Life): number {
  return life.log.filter((e) => MOVE_KEYS.has(e.key)).length;
}

function workLine(life: Life): string {
  const started = life.flags[FLAG_JOB_START_AGE];
  if (typeof started !== 'number') return t('ui.death.work.none');
  if (life.job !== null) {
    const n = Math.max(1, life.age - started);
    return t('ui.death.work.years', { title: t(life.job.titleKey), n, years: yearsWord(n) });
  }
  if (life.flags[FLAG_RETIRED]) {
    const retiredAt = life.log.find((e) => e.key === RETIRE_KEY)?.age ?? life.age;
    const n = Math.max(1, retiredAt - started);
    return t('ui.death.work.retired', { n, years: yearsWord(n) });
  }
  return t('ui.death.work.none');
}

function familyLine(life: Life): string {
  const partner = life.partner ? t(`ui.death.family.partner.${life.sex}`) : '';
  const children =
    life.children > 0 ? t('ui.death.family.children', { n: life.children, word: childrenWord(life.children) }) : '';
  if (partner && children) return t('ui.death.family.both', { partner, children });
  return partner || children || t('ui.death.family.none');
}

export function certificateFacts(life: Life, ds: Dataset, skipHeavy: boolean): CertificateFacts {
  const from = birthCountry(life, ds);
  const to = countryName(ds, life.iso3);
  const died = life.birthYear + life.age;
  const span =
    from === to
      ? t('ui.death.span.same', { country: to, born: life.birthYear, died })
      : t('ui.death.span', { from, to, born: life.birthYear, died });
  const death = life.death;
  const cause =
    death === null ? '' : death.heavy && skipHeavy ? t('ui.death.cause.hidden') : t(`cause.${death.cause}`);
  const moves = movesCount(life);
  return {
    name: life.name,
    span,
    lived: years(life.age),
    cause,
    education: t(`education.${life.education}`),
    work: workLine(life),
    family: familyLine(life),
    moves: moves === 0 ? t('ui.death.moves.none') : t('ui.death.moves.n', { n: moves }),
  };
}

/** Строка мечты для свидетельства и досье: сбылась (с возрастом), не сбылась, не было. */
export function dreamLine(life: Life): string {
  const dream = life.dream;
  if (dream === null) return t('dream.none');
  const name = t(`dream.${dream.id}`);
  if (dream.achievedAt !== null) return t('ui.death.dream.done', { dream: name, age: dream.achievedAt, years: yearsWord(dream.achievedAt) });
  return t('ui.death.dream.failed', { dream: name });
}

/** Ожидаемая продолжительность жизни для пола и года рождения, округлённая. */
export function expectedYears(ds: Dataset, iso3: string, life: Life): number {
  return Math.round(getValue(ds, iso3, life.sex === 'f' ? 'lifeExpF' : 'lifeExpM', life.birthYear).v);
}

export function countryLabel(ds: Dataset, iso3: string): string {
  try {
    return getCountry(ds, iso3).nameRu;
  } catch {
    return iso3;
  }
}

/** Последние слова из флагов жизни. */
export function lastWordsOf(life: Life): string {
  const v = life.flags[FLAG_LAST_WORDS];
  return typeof v === 'string' ? v : '';
}

/** Важные годы из флагов жизни (строка «2,4,6»). */
export function keyYearsOf(life: Life): number[] {
  const v = life.flags[FLAG_KEY_YEARS];
  if (typeof v !== 'string' || v === '') return [];
  return v
    .split(',')
    .map((s) => Number(s))
    .filter((n) => Number.isInteger(n) && n >= 0);
}
